import React, { useMemo } from 'react';
import { AreaChart, Area, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
import { Globe } from 'lucide-react';
import MetricCard from './MetricCard';
import StatDisplay from './StatDisplay';
import { getPercentChange, axisStyle } from './marketUtils';

const ForeignIndicesCard = ({ data: { vxus, efa, eem } }) => {
  const chartData = useMemo(() => {
    if (!vxus || !efa || !eem) return [];

    // Get the baseline values for percentage calculations
    const baselineVXUS = vxus[0]?.close || 0;
    const baselineEFA = efa[0]?.close || 0;
    const baselineEEM = eem[0]?.close || 0;

    return vxus.map((point, i) => {
      // Calculate percentage change from baseline for each index
      const vxusChange = ((point.close - baselineVXUS) / baselineVXUS) * 100;
      const efaChange = ((efa[i]?.close - baselineEFA) / baselineEFA) * 100;
      const eemChange = ((eem[i]?.close - baselineEEM) / baselineEEM) * 100;

      return {
        timestamp: point.timestamp,
        VXUS: vxusChange,
        EFA: efaChange,
        EEM: eemChange,
        // Calculate cumulative impact
        Total: vxusChange + efaChange + eemChange
      };
    });
  }, [vxus, efa, eem]);

  const latestData = {
    vxus: vxus[vxus.length - 1]?.close || 0,
    efa: efa[efa.length - 1]?.close || 0,
    eem: eem[eem.length - 1]?.close || 0
  };

  const weekPriorData = {
    vxus: vxus[vxus.length - 6]?.close || 0,
    efa: efa[efa.length - 6]?.close || 0,
    eem: eem[eem.length - 6]?.close || 0
  };

  const changes = {
    vxus: getPercentChange(latestData.vxus, weekPriorData.vxus),
    efa: getPercentChange(latestData.efa, weekPriorData.efa),
    eem: getPercentChange(latestData.eem, weekPriorData.eem)
  };

  // Calculate relative strength indicators
  const relativeStrength = useMemo(() => {
    const lastPoint = chartData[chartData.length - 1] || {};
    return {
      developed: lastPoint.EFA > lastPoint.EEM ? 'Developed' : 'Emerging',
      performance: Math.abs(lastPoint.EFA - lastPoint.EEM).toFixed(2)
    };
  }, [chartData]);

  const headerRight = (
    <div className="flex items-center gap-2 py-0.5 px-2 rounded-full text-blue-700 border border-blue-600 font-medium">
      {`${relativeStrength.developed} Leading`}
      <Globe className="w-5 h-5" />
    </div>
  );

  const statsContent = (
    <div className="grid grid-cols-3 gap-4">
      <div className="p-2">
        <StatDisplay
          title="VXUS"
          value={latestData.vxus.toFixed(2)}
          change={parseFloat(changes.vxus)}
          period="1w"
        />
      </div>
      <div className="p-2">
        <StatDisplay
          title="EFA"
          value={latestData.efa.toFixed(2)}
          change={parseFloat(changes.efa)}
          period="1w"
        />
      </div>
      <div className="p-2">
        <StatDisplay
          title="EEM"
          value={latestData.eem.toFixed(2)}
          change={parseFloat(changes.eem)}
          period="1w"
        />
      </div>
    </div>
  );

  const chartContent = (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} stackOffset="none">
        <XAxis
          {...axisStyle}
          dataKey="timestamp"
          height={20}
        />
        <YAxis
          {...axisStyle}
          domain={['auto', 'auto']}
          tickFormatter={(value) => `${value.toFixed(1)}%`}
        />
        <Tooltip
          formatter={(value) => [`${value.toFixed(2)}%`]}
          labelFormatter={(label) => `Date: ${label}`}
        />
        <Area
          type="monotone"
          dataKey="EEM"
          stackId="1"
          stroke="#ef4444"
          fill="#fee2e2"
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="EFA"
          stackId="1"
          stroke="#3b82f6"
          fill="#dbeafe"
          isAnimationActive={false}
        />
        <Area
          type="monotone"
          dataKey="VXUS"
          stackId="1"
          stroke="#22c55e"
          fill="#dcfce7"
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  const bottomContent = (
    <div className="grid grid-cols-3 gap-4 px-2">
      <div>
        <div className="text-lg card-value text-gray-900">
          {`${relativeStrength.performance}%`}
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">Dev/Em Spread</div>
      </div>
      <div>
        <div className="text-lg card-value text-gray-900">
          {chartData[chartData.length - 1]?.Total.toFixed(2)}%
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">Cumulative Change</div>
      </div>
      <div>
        <div className="text-lg card-value text-gray-900">
          {(Math.max(
            Math.abs(changes.vxus),
            Math.abs(changes.efa),
            Math.abs(changes.eem)
          )).toFixed(2)}%
        </div>
        <div className="text-sm text-gray-500 mt-1 card-label">Max Weekly Move</div>
      </div>
    </div>
  );

  return (
    <MetricCard
      title="Foreign Markets"
      headerRight={headerRight}
      statsContent={statsContent}
      chartContent={chartContent}
      bottomContent={bottomContent}
      titleIcon={<Globe size={24} color="#4f46e5" strokeWidth={2} />}
    />
  );
};

export default ForeignIndicesCard;